import React, { useState, useRef } from 'react';
import {
  Flex, Text, Box, ActionNote, StateTaxFormInfo,
} from '../../components';
import * as S from '../../styles';
import { STEPS } from '../commonStateTaxData';
import {
  basicInfoQuestions,
  worksheetStatusQuestions,
  worksheetShortTextQuestions,
  domicileStateQuestion,
  stateOfResidenceQuestion,
} from './data';
import FormConsumer from '../../collect-info/FormConsumers';
import CollapsibleQuestions from '../CollapsibleQuestions';
import StateTaxFormLayout from '../common/state-tax-content-layout';

function LegacyForm({
  prefilledData = {},
  saveFormData,
  isSubmitting,
  scrollTop,
  handleSigning,
  viewResultAsManager,
  data,
  companyStaff,
  getPdfUrl,
  getInitialQuestions,
  resetInitialQuestions,
  formLink,
  isOnboardingV2,
}) {
  const [currentStep, setCurrentStep] = useState(STEPS.step1);

  const formatFullName = () => {
    const firstName = `${`${prefilledData.employee_first_name} `}` || '';
    const middleInitial = prefilledData.employee_middle_name;
    const lastName = prefilledData.employee_last_name || '';
    return `${firstName}${middleInitial ? `${middleInitial} ` : ''}${lastName}`;
  };
  prefilledData.employee_name = formatFullName();

  const basicInfoForm = useRef();
  const basicInfoInitQ = getInitialQuestions(basicInfoQuestions);
  const [basicInfo, setBasicInfo] = useState(basicInfoInitQ);

  const [isExemptFromWithholding, setIsExemptFromWithholding] = useState(data.is_exempt || false);
  const [worksheetOpen, setWorksheetOpen] = useState(false);

  const allowanceForm = useRef();

  const [initialAllowances, setInitialAllowances] = useState(data.total_number_exemptions || '');
  const [initialAllowancesError, setInitialAllowancesError] = useState(false);
  const [initialAllowancesValueError, setInitialAllowancesValueError] = useState(false);

  const worksheetForm = useRef();
  const worksheetStatusInitQ = getInitialQuestions(worksheetStatusQuestions);
  const [wsStatus, setWsStatus] = useState(worksheetStatusInitQ);

  const worksheetShortTextInitQ = getInitialQuestions(worksheetShortTextQuestions);
  const [wsShortText, setWsShortText] = useState(worksheetShortTextInitQ);

  const exemptionForm = useRef();

  const [maxNumExemptions, setMaxNumExemptions] = useState(data.max_num_exemptions || 0);

  const getExemptionValue = (federalAGI, federalFilingStatus) => {
    let exemptionValue;

    if (federalAGI === 'lteq_100') {
      exemptionValue = 3200;
    } else if (
      (federalFilingStatus === 'married_or_widow' || federalFilingStatus === 'head_of_household')
      && (federalAGI === 'gt_100_lteq_125' || federalAGI === 'gt_125_lteq_150')
    ) {
      exemptionValue = 3200;
    } else if (
      (federalFilingStatus === 'married_or_wi dow' || federalFilingStatus === 'head_of_household')
      && federalAGI === 'gt_150_lteq_175'
    ) {
      exemptionValue = 1600;
    } else if (
      (federalFilingStatus === 'single_or_married' && federalAGI === 'gt_125_lteq_150')
      || ((federalFilingStatus === 'married_or_widow' || federalFilingStatus === 'head_of_household')
        && federalAGI === 'gt_175_lteq_200')
    ) {
      exemptionValue = 800;
    } else {
      exemptionValue = 0;
    }

    return exemptionValue;
  };

  const calculateMaxNumExemptions = () => {
    const res = [];

    const federalAGI = wsStatus[0].answer.answer.federal_income || '';
    const federalFilingStatus = basicInfo[7].answer.answer.federal_filing_status || '';
    const numExemptions = parseInt(wsShortText[0].answer.answer.personal_exemptions_claimed) || 0;
    const exemptionValue = getExemptionValue(federalAGI, federalFilingStatus);

    const lineA = numExemptions * exemptionValue;
    res.push(lineA);

    const dependentsOver65 = wsShortText[1].answer.answer.additional_exemptions_age || 0;
    const lineB = parseInt(dependentsOver65) * exemptionValue;
    res.push(lineB);

    const lineC = parseInt(wsShortText[2].answer.answer.itemized_deductions) || 0;
    res.push(lineC);

    const lineD = wsShortText[3].answer.answer.additional_exemptions_spouse !== '' ? 1000 : 0;
    res.push(lineD);

    const lineE = lineA + lineB + lineC + lineD;
    res.push(lineE);

    const lineF = Math.trunc(lineE / 3200);
    res.push(lineF);

    return res;
  };

  const renderBasicInfoForm = (isReviewStep) => (
    <div>
      <FormConsumer
        questions={basicInfo}
        {...(isReviewStep && { viewResultAsManager: true, reviewActions: [] })}
        onQuestionChange={({ question, value }) => {
          const questions = basicInfo.map((q) => {
            if (q.id !== question.id) return q;
            const answer = {
              ...q.answer,
              answer: {
                ...q.answer.answer,
                [q.id]: value,
              },
            };
            const updatedQuestion = { ...q, answer };
            return updatedQuestion;
          });
          setBasicInfo(questions);
        }}
        ref={basicInfoForm}
      />
      <Flex column>
        <S.QuestionLabel mb="7px" isRequired>
          I wish to be exempt from withholding.
        </S.QuestionLabel>
        <S.QuestionWrapper mb="20px">
          <label>
            <Flex alignItems="center">
              <S.Input
                type="radio"
                value={isExemptFromWithholding}
                checked={isExemptFromWithholding}
                disabled={isReviewStep}
                width="auto"
                onChange={(e) => {
                  setIsExemptFromWithholding(!isExemptFromWithholding);
                }}
              />
              <Box ml={2} />
              Yes
            </Flex>
          </label>
          <label>
            <Flex alignItems="center">
              <S.Input
                type="radio"
                value={!isExemptFromWithholding}
                checked={!isExemptFromWithholding}
                disabled={isReviewStep}
                width="auto"
                onChange={(e) => {
                  setIsExemptFromWithholding(!isExemptFromWithholding);
                }}
              />
              <Box ml={2} />
              No
            </Flex>
          </label>
        </S.QuestionWrapper>
      </Flex>
    </div>
  );

  const renderWorksheetContent = (isReviewStep) => (
    <Flex column>
      <FormConsumer
        questions={wsStatus}
        {...(isReviewStep && { viewResultAsManager: true, reviewActions: [] })}
        onQuestionChange={({ question, value }) => {
          const questions = wsStatus.map((q) => {
            if (q.id !== question.id) return q;
            const answer = {
              ...q.answer,
              answer: {
                ...q.answer.answer,
                [q.id]: value,
              },
            };
            const updatedQuestion = { ...q, answer };
            return updatedQuestion;
          });
          setWsStatus(questions);
        }}
        ref={worksheetForm}
      />
      <FormConsumer
        questions={wsShortText}
        {...(isReviewStep && { viewResultAsManager: true, reviewActions: [] })}
        onQuestionChange={({ question, value }) => {
          const questions = wsShortText.map((q) => {
            if (q.id !== question.id) return q;
            const answer = {
              ...q.answer,
              answer: {
                ...q.answer.answer,
                [q.id]: value,
              },
            };
            const updatedQuestion = { ...q, answer };
            return updatedQuestion;
          });
          setWsShortText(questions);
        }}
        ref={worksheetForm}
      />
    </Flex>
  );

  const renderDropdown = (isReviewStep) => (
    <Flex column>
      <S.QuestionLabel mb="7px" isRequired>
        Total number of exemptions you are claiming not to exceed the maximum number of exemptions below
      </S.QuestionLabel>
      <S.QuestionWrapper>
        <Flex mb="10px">
          <S.Input
            type="text"
            value={initialAllowances}
            onChange={(e) => setInitialAllowances(e.target.value)}
            style={{ borderColor: initialAllowancesError || initialAllowancesValueError ? 'red' : '' }}
          />
        </Flex>
      </S.QuestionWrapper>
      {initialAllowancesError && (
      <Flex justifyContent="flex-start">
        <Text color="red">You may not claim more allowances than you are entitled to</Text>
      </Flex>
      )}
      {initialAllowancesValueError && (
      <Flex justifyContent="flex-start">
        <Text color="red">Please enter a valid response</Text>
      </Flex>
      )}
      <CollapsibleQuestions
        title="If you need to calculate your total number of allowances, use this worksheet."
        handleClick={() => {
          if (!isReviewStep) {
            setWorksheetOpen(!worksheetOpen);
          }
        }}
        open={worksheetOpen || isReviewStep}
      >
        {!isExemptFromWithholding && renderWorksheetContent(isReviewStep)}
        <S.QuestionLabel mb="7px">
          This is the maximum number of exemptions you may claim for tax withholding purposes
        </S.QuestionLabel>
        <S.QuestionWrapper>
          <Flex mb="10px">
            <S.Input
              type="text"
              value={worksheetOpen ? calculateMaxNumExemptions()[5] : maxNumExemptions}
              onChange={(e) => setMaxNumExemptions(e.target.value)}
              disabled
            />
          </Flex>
        </S.QuestionWrapper>
      </CollapsibleQuestions>
    </Flex>
  );

  const [noMDIncomeTax, setNoMDIncomeTax] = useState(data.owes_no_MD_income_tax || false);
  const [domiciled1, setDomiciled1] = useState(data.domiciled_in_DC_VA_WV || false);
  const [domiciled2, setDomiciled2] = useState(data.domiciled_in_PA || false);
  const [liveInPA1, setLiveInPA1] = useState(data.live_in_York_Adams_counties || false);
  const [liveInPA2, setLiveInPA2] = useState(data.live_in_PA_no_tax_on_MD_residents || false);
  const [serviceMember, setServiceMember] = useState(data.servicemembers_civil_relief_act || false);

  const [yearEffective, setYearEffective] = useState(data.year_applicable || '');
  const [yearEffectiveError, setYearEffectiveError] = useState(false);

  const domicileStateInitQ = getInitialQuestions(domicileStateQuestion);
  const [domicileState, setDomicileState] = useState(domicileStateInitQ);

  const stateOfResidenceInitQ = getInitialQuestions(stateOfResidenceQuestion);
  const [stateOfResidence, setStateOfResidence] = useState(stateOfResidenceInitQ);

  const renderDomicileState = (isReviewStep) => (
    <FormConsumer
      questions={domicileState}
      {...(isReviewStep && { viewResultAsManager: true, reviewActions: [] })}
      onQuestionChange={({ question, value }) => {
        const questions = domicileState.map((q) => {
          if (q.id !== question.id) return q;
          const answer = {
            ...q.answer,
            answer: {
              ...q.answer.answer,
              [q.id]: value,
            },
          };
          const updatedQuestion = { ...q, answer };
          return updatedQuestion;
        });
        setDomicileState(questions);
      }}
      ref={exemptionForm}
    />
  );

  const renderStateOfRes = (isReviewStep) => (
    <FormConsumer
      questions={stateOfResidence}
      {...(isReviewStep && { viewResultAsManager: true, reviewActions: [] })}
      onQuestionChange={({ question, value }) => {
        const questions = stateOfResidence.map((q) => {
          if (q.id !== question.id) return q;
          const answer = {
            ...q.answer,
            answer: {
              ...q.answer.answer,
              [q.id]: value,
            },
          };
          const updatedQuestion = { ...q, answer };
          return updatedQuestion;
        });
        setStateOfResidence(questions);
      }}
      ref={exemptionForm}
    />
  );

  const renderYearEffective = (isReviewStep) => (
    <Flex column>
      <S.QuestionLabel mb="7px" isRequired={noMDIncomeTax}>
        Enter the year applicable (year effective)
      </S.QuestionLabel>
      <S.QuestionWrapper>
        <Flex mb="10px">
          <S.Input
            type="text"
            value={yearEffective}
            onChange={(e) => setYearEffective(e.target.value)}
            disabled={isReviewStep}
            style={{ borderColor: yearEffectiveError ? 'red' : '' }}
          />
        </Flex>
      </S.QuestionWrapper>
      {yearEffectiveError && (
      <Flex justifyContent="flex-start">
        <Text color="red">Please enter a valid year</Text>
      </Flex>
      )}
    </Flex>
  );

  const renderExemptions = (isReviewStep) => (
    <Flex column>
      <Text mb="10px" style={{ fontWeight: '600', fontSize: '15px' }}>
        Exemption from withholding
      </Text>
      <Flex column>
        <S.QuestionLabel mb="15px" mt="10px" isRequired>
          To claim exemption from withholding, see instructions on Maryland Form MW507, then check boxes here that
          apply.
        </S.QuestionLabel>
        <S.QuestionWrapper mb="20px">
          <label mb="10px">
            <Flex mt="10px" style={{ marginBottom: '15px' }}>
              <S.Input
                style={{ height: '22px' }}
                type="checkbox"
                value={noMDIncomeTax}
                checked={noMDIncomeTax}
                disabled={isReviewStep}
                width="auto"
                onChange={(e) => {
                  setNoMDIncomeTax(!noMDIncomeTax);
                }}
              />
              <Box ml={1} />
              Last year I did not owe any Maryland income tax and had a right to a full refund of all income tax
              withheld AND this year I do not expect to owe any Maryland income tax and expect to have the right to a
              full refund of all income tax withheld. (This includes seasonal and student employees whose annual
              income will be below the minimum filing requirements).
            </Flex>
          </label>
          {noMDIncomeTax && renderYearEffective(isReviewStep)}
          <label mb="10px">
            <Flex style={{ marginBottom: '15px' }}>
              <S.Input
                style={{ height: '22px' }}
                type="checkbox"
                value={domiciled1}
                checked={domiciled1}
                disabled={isReviewStep}
                width="auto"
                onChange={(e) => {
                  setDomiciled1(!domiciled1);
                }}
              />
              <Box ml={2} />
              I claim exemption from withholding because I am domiciled in the District of Columbia, Virginia, or West Virginia.
            </Flex>
          </label>
          {domiciled1 && renderDomicileState(isReviewStep)}
          <label mb="10px">
            <Flex style={{ marginBottom: '15px' }}>
              <S.Input
                style={{ height: '22px' }}
                type="checkbox"
                value={domiciled2}
                checked={domiciled2}
                disabled={isReviewStep}
                width="auto"
                onChange={(e) => {
                  setDomiciled2(!domiciled2);
                }}
              />
              <Box ml={2} />
              I claim exemption from Maryland state withholding because I am domiciled in the Commonwealth of Pennsylvania
              and I do not maintain a place of abode in Maryland as described in the instructions on Form MW507.
            </Flex>
          </label>
          <label mb="10px">
            <Flex style={{ marginBottom: '15px' }}>
              <S.Input
                style={{ height: '22px' }}
                type="checkbox"
                value={liveInPA1}
                checked={liveInPA1}
                disabled={isReviewStep}
                width="auto"
                onChange={(e) => {
                  setLiveInPA1(!liveInPA1);
                }}
              />
              <Box ml={2} />
              I claim exemption from Maryland local tax because I live in a local Pennsylvania jurisdiction within York or Adams counties.
            </Flex>
          </label>
          <label mb="10px">
            <Flex style={{ marginBottom: '15px' }}>
              <S.Input
                style={{ height: '22px' }}
                type="checkbox"
                value={liveInPA2}
                checked={liveInPA2}
                disabled={isReviewStep}
                width="auto"
                onChange={(e) => {
                  setLiveInPA2(!liveInPA2);
                }}
              />
              <Box ml={2} />
              I claim exemption from Maryland local tax because I live in a local Pennsylvania jurisdiction that does not impose an earnings or income tax on Maryland resident
            </Flex>
          </label>
          <label mb="10px">
            <Flex style={{ marginBottom: '15px' }}>
              <S.Input
                style={{ height: '22px' }}
                type="checkbox"
                value={serviceMember}
                checked={serviceMember}
                disabled={isReviewStep}
                width="auto"
                onChange={(e) => {
                  setServiceMember(!serviceMember);
                }}
              />
              <Box ml={2} />
              I am not subject to Maryland withholding because I meet the requirements set forth under the Servicemembers
              Civil Relief Act, as amended by the Military Spouses Residency Relief Act.
            </Flex>
          </label>
          {serviceMember && renderStateOfRes(isReviewStep)}
        </S.QuestionWrapper>
      </Flex>
    </Flex>
  );

  const renderStep1 = () => {
    let disabled;
    let disabledText;
    if (viewResultAsManager) {
      disabled = true;
      disabledText = 'This step has already been completed.';
    } else if (companyStaff && companyStaff.id) {
      disabled = true;
      disabledText = 'This step should be completed by the employee.';
    }

    return (
      <Flex column>
        {
          isOnboardingV2 ? null : (
            <Text fontSize="22px" bold>
              Fill state tax form
            </Text>
          )
        }
        <a href={formLink} target="_blank" rel="noreferrer">
          View original form and instructions
        </a>
        <Flex column mt={16}>
          {renderBasicInfoForm()}
          {isExemptFromWithholding && renderExemptions()}
          {!isExemptFromWithholding && renderDropdown()}
        </Flex>
        {viewResultAsManager && companyStaff.pii_permission === 'full_access' && (
          <Flex column mt={10} mb={10}>
            <Flex alignItems="center">
              <Text mr={16}>Download signed documents</Text>
              <a href={getPdfUrl()} target="_blank" rel="noopener noreferrer">
                <img src="/j/images/onboarding/download.svg" />
              </a>
            </Flex>
          </Flex>
        )}
        <S.Button
          disabled={disabled}
          onClick={() => {
            let err = null;
            if (basicInfoForm && basicInfoForm.current) {
              basicInfoForm.current.validate((error) => {
                if (error) err = error;
              });
            }
            if (worksheetForm && worksheetForm.current) {
              worksheetForm.current.validate((error) => {
                if (error) err = error;
              });
            }
            if (exemptionForm && exemptionForm.current) {
              exemptionForm.current.validate((error) => {
                if (error) err = error;
              });
            }
            if (allowanceForm && allowanceForm.current) {
              allowanceForm.current.validate((error) => {
                if (error) err = error;
              });
            }
            if (!isExemptFromWithholding) {
              if (initialAllowances > calculateMaxNumExemptions()[5]) {
                err = true;
                setInitialAllowancesError(true);
              } else {
                setInitialAllowancesError(false);
              }
              if (!/^\d*$/.test(initialAllowances)) {
                err = true;
                setInitialAllowancesValueError(true);
              } else {
                setInitialAllowancesValueError(false);
              }
            } else if (yearEffective && !/^\d{4}$/.test(yearEffective)) {
              err = true;
              setYearEffectiveError(true);
            } else {
              setYearEffectiveError(false);
            }
            if (err) {

            } else {
              // No error, proceed to save
              // - save answers to server
              // - processed to next step

              const basicInfoData = basicInfo.reduce((acc, question) => ({ ...acc, ...question.answer.answer }), {});
              const exemptionStatusData = { is_exempt: isExemptFromWithholding };

              let noMDIncomeTaxTemp = '';
              let domiciled1Temp = '';
              let domiciled2Temp = '';
              let liveInPA1Temp = '';
              let liveInPA2Temp = '';
              let serviceMemberTemp = '';
              let initAllowancesTemp = '';
              let maxNumExemptionsTemp = '';
              let yearEffectiveTemp = '';

              let line1PartATemp = '';
              let line1PartBTemp = '';
              let line1PartCTemp = '';
              let line1PartDTemp = '';
              let line1PartETemp = '';

              let domicileStateData;
              let stateOfResData;

              let wsStatusData;
              let wsShortTextData;

              if (isExemptFromWithholding) {
                noMDIncomeTaxTemp = noMDIncomeTax;
                domiciled1Temp = domiciled1;
                domiciled2Temp = domiciled2;
                liveInPA1Temp = liveInPA1;
                liveInPA2Temp = liveInPA2;
                serviceMemberTemp = serviceMember;

                if (domiciled1) {
                  domicileStateData = domicileState.reduce((acc, question) => ({ ...acc, ...question.answer.answer }), {});
                } else {
                  domicileStateData = resetInitialQuestions(domicileStateQuestion).reduce((acc, question) => ({ ...acc, ...question.answer.answer }), {});
                }
                if (serviceMember) {
                  stateOfResData = stateOfResidence.reduce((acc, question) => ({ ...acc, ...question.answer.answer }), {});
                } else {
                  stateOfResData = resetInitialQuestions(stateOfResidenceQuestion).reduce((acc, question) => ({ ...acc, ...question.answer.answer }), {});
                }
                if (noMDIncomeTax) {
                  yearEffectiveTemp = yearEffective;
                }
                wsStatusData = resetInitialQuestions(worksheetStatusQuestions).reduce((acc, question) => ({ ...acc, ...question.answer.answer }), {});
                wsShortTextData = resetInitialQuestions(worksheetShortTextQuestions).reduce((acc, question) => ({ ...acc, ...question.answer.answer }), {});
              } else {
                const maxNumExemptionsVal = calculateMaxNumExemptions();
                line1PartATemp = maxNumExemptionsVal[0].toString();
                line1PartBTemp = maxNumExemptionsVal[1].toString();
                line1PartCTemp = maxNumExemptionsVal[2].toString();
                line1PartDTemp = maxNumExemptionsVal[3].toString();
                line1PartETemp = maxNumExemptionsVal[4].toString();
                maxNumExemptionsTemp = maxNumExemptionsVal[5].toString();

                initAllowancesTemp = initialAllowances;
                wsStatusData = wsStatus.reduce((acc, question) => ({ ...acc, ...question.answer.answer }), {});
                wsShortTextData = wsShortText.reduce((acc, question) => ({ ...acc, ...question.answer.answer }), {});

                domicileStateData = resetInitialQuestions(domicileStateQuestion).reduce((acc, question) => ({ ...acc, ...question.answer.answer }), {});
                stateOfResData = resetInitialQuestions(stateOfResidenceQuestion).reduce((acc, question) => ({ ...acc, ...question.answer.answer }), {});
              }

              const exemptionSelectData = {
                owes_no_MD_income_tax: noMDIncomeTaxTemp,
                domiciled_in_DC_VA_WV: domiciled1Temp,
                domiciled_in_PA: domiciled2Temp,
                live_in_York_Adams_counties: liveInPA1Temp,
                live_in_PA_no_tax_on_MD_residents: liveInPA2Temp,
                servicemembers_civil_relief_act: serviceMemberTemp,
                year_applicable: yearEffectiveTemp,
              };
              const calculatedData = {
                total_number_exemptions: initAllowancesTemp,
                max_num_exemptions: maxNumExemptionsTemp,
                line_1_part_a: line1PartATemp,
                line_1_part_b: line1PartBTemp,
                line_1_part_c: line1PartCTemp,
                line_1_part_d: line1PartDTemp,
                line_1_part_e: line1PartETemp,
              };
              const allExemptionData = {
                ...exemptionStatusData,
                ...exemptionSelectData,
                ...domicileStateData,
                ...stateOfResData,
              };
              const allWorksheetData = { ...wsStatusData, ...wsShortTextData };

              const data = {
                ...basicInfoData, ...allExemptionData, ...allWorksheetData, ...calculatedData,
              };
              saveFormData(data, () => {
                setCurrentStep(STEPS.step2);
                scrollTop();
              });
            }
          }}
          loading={isSubmitting}
        >
          Next
        </S.Button>
        {disabledText && (
          <Flex justifyContent="flex-end">
            <ActionNote text={disabledText} />
          </Flex>
        )}
      </Flex>
    );
  };

  const renderStep2 = () => (
    <Flex column>
      <Text fontSize="22px" bold>
        Review
      </Text>
      <StateTaxFormInfo />
      <a href={formLink} target="_blank" rel="noreferrer">
        View original form and instructions
      </a>
      <Flex column mt={16}>
        {renderBasicInfoForm(true)}
        {isExemptFromWithholding && renderExemptions(true)}
        {!isExemptFromWithholding && renderDropdown(true)}
      </Flex>
      <Flex mt="10px">
        <S.SecondaryButton
          disabled={viewResultAsManager}
          onClick={() => {
            setCurrentStep(STEPS.step1);
            scrollTop();
          }}
        >
          Back
        </S.SecondaryButton>
        <Flex flex={1} column ml={2}>
          <S.Button
            disabled={viewResultAsManager}
            onClick={() => {
              handleSigning();
              setCurrentStep(STEPS.step3);
              scrollTop();
            }}
            loading={isSubmitting}
          >
            Finalize and sign
          </S.Button>
        </Flex>
      </Flex>
    </Flex>
  );

  const renderStep3 = () => (
    <Flex column>
      <Text fontSize="22px" bold>
        Finalize and sign
      </Text>
      <Flex column mt={44}>
        <div id="iframe-container" />
      </Flex>
    </Flex>
  );

  const isSigningStep = currentStep === STEPS.step3;

  return (
    <StateTaxFormLayout isOnboardingV2={isOnboardingV2} isSigningStep={isSigningStep}>
      {currentStep === STEPS.step1 && renderStep1()}
      {currentStep === STEPS.step2 && renderStep2()}
      {currentStep === STEPS.step3 && renderStep3()}
    </StateTaxFormLayout>

  );
}

export default LegacyForm;
